import { useState } from 'react';
import { paymentMethods } from '../../../mocks/orders';

export default function PaymentSection() {
  const [selected, setSelected] = useState('card');

  return (
    <section id="payment" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          {/* Left content */}
          <div>
            <span className="inline-block bg-[#FF6B35]/10 text-[#FF6B35] text-xs font-bold px-3 py-1 rounded-full mb-4 tracking-widest uppercase">
              Online Payment
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 leading-tight" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.02em' }}>
              Pay Your Way,<br />
              <span className="text-[#FF6B35]">Every Way</span>
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-8">
              We support multiple payment methods so you can checkout with ease — pay with your credit or debit card, or simply settle on delivery in Georgetown, Guyana.
            </p>

            {/* Payment method cards */}
            <div className="space-y-3">
              {paymentMethods.map((method) => (
                <button
                  key={method.id}
                  onClick={() => setSelected(method.id)}
                  className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left cursor-pointer ${
                    selected === method.id
                      ? 'border-[#FF6B35] bg-[#FF6B35]/5'
                      : 'border-gray-100 hover:border-gray-200'
                  }`}
                >
                  <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${selected === method.id ? 'bg-[#FF6B35] text-white' : 'bg-gray-100 text-gray-500'}`}>
                    <i className={`${method.icon} text-xl`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-900 text-sm">{method.label}</span>
                      {method.popular && (
                        <span className="bg-[#FF6B35] text-white text-xs px-2 py-0.5 rounded-full font-semibold">Popular</span>
                      )}
                    </div>
                    <span className="text-gray-400 text-xs">{method.description}</span>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selected === method.id ? 'border-[#FF6B35] bg-[#FF6B35]' : 'border-gray-300'}`}>
                    {selected === method.id && <div className="w-2 h-2 bg-white rounded-full" />}
                  </div>
                </button>
              ))}
            </div>

            {/* Security badges */}
            <div className="flex items-center gap-3 mt-6 p-4 bg-gray-50 rounded-xl">
              <div className="w-8 h-8 flex items-center justify-center text-green-600">
                <i className="ri-shield-check-line text-xl" />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-800">256-bit SSL Encryption</p>
                <p className="text-xs text-gray-400">Your payment info is always secure</p>
              </div>
              <div className="ml-auto flex gap-2">
                {['VISA', 'MC'].map((brand) => (
                  <span key={brand} className="text-xs font-black text-gray-400 border border-gray-200 px-2 py-1 rounded">
                    {brand}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right — Checkout card mockup */}
          <div className="relative">
            <div className="bg-[#0D0D0D] rounded-3xl p-8 text-white">
              {/* Order Summary */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-lg">Order Summary</h3>
                <span className="text-[#FF6B35] text-sm font-semibold">SDG-20847</span>
              </div>

              <div className="space-y-3 mb-6">
                {[
                  { name: 'Banks Beer x6', price: 4800 },
                  { name: 'El Dorado Rum 750ml', price: 4500 },
                  { name: 'Carib Lager x3', price: 2250 },
                ].map((item) => (
                  <div key={item.name} className="flex justify-between text-sm">
                    <span className="text-white/70">{item.name}</span>
                    <span className="font-semibold">GYD ${item.price.toLocaleString()}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-white/10 pt-4 mb-6 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Subtotal</span>
                  <span>GYD $11,550</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Delivery Fee</span>
                  <span className="text-green-400">FREE</span>
                </div>
                <div className="flex justify-between font-black text-lg">
                  <span>Total</span>
                  <span className="text-[#FF6B35]">GYD $11,550</span>
                </div>
              </div>

              {/* Selected payment display */}
              <div className="bg-white/10 rounded-xl p-4 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center text-[#FF6B35]">
                  <i className={`${paymentMethods.find(m => m.id === selected)?.icon || 'ri-bank-card-line'} text-lg`} />
                </div>
                <div>
                  <p className="text-sm font-semibold">{paymentMethods.find(m => m.id === selected)?.label}</p>
                  <p className="text-xs text-white/50">{paymentMethods.find(m => m.id === selected)?.description}</p>
                </div>
                <button className="ml-auto text-[#FF6B35] text-xs font-semibold cursor-pointer whitespace-nowrap">Change</button>
              </div>

              <button className="w-full bg-[#FF6B35] text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 hover:bg-[#e85d2a] transition-colors cursor-pointer">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-secure-payment-line" />
                </div>
                Pay Now
              </button>
            </div>

            {/* Floating badge */}
            <div className="absolute -top-4 -right-4 bg-green-500 text-white text-xs font-bold px-3 py-2 rounded-full">
              100% Secure
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
